// Financial Reporting suite — "Create report" modal. The period is
// prefilled (the series' next unfiled period) and read-only; computed
// sections are rendered server-side, so the only editable inputs here are
// the narrative fields the preparer authors (FR-Q4 material developments,
// FR-Q2 findings).

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button, Textarea } from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import { extractErrorMessage } from "@/api/client";
import type { ReportingSeriesCardDto } from "@/api/portfolioAdmin/financialReportingApi";
import { reportingPeriodLabel } from "./reportingFormat";

const NARRATIVE_FIELD: Record<string, { label: string; helper: string }> = {
  "FR-Q4": {
    label: "Material developments",
    helper:
      "Preparer-authored. Appears in the Buyer Financial Performance Update section.",
  },
  "FR-Q2": {
    label: "Findings narrative",
    helper:
      "Preparer-authored. Leave blank to use the computed summary sentence.",
  },
  // Defaults & Workout — DW-M1 "status and next steps".
  "DW-M1": {
    label: "Status and next steps",
    helper:
      "Preparer-authored. Leave blank to use the computed case summary " +
      "(clean loans render the clean bill of health automatically).",
  },
  // Vendor Program — VG-M1 preparer commentary.
  "VG-M1": {
    label: "Preparer commentary",
    helper:
      "Preparer-authored. Appears as the Preparer Narrative section of the " +
      "goal-progress report; leave blank to file the computed sections only.",
  },
};

interface CreateReportModalProps {
  card: ReportingSeriesCardDto;
  open: boolean;
  onClose: () => void;
  onCreate: (narrative: string | null) => Promise<unknown>;
  isPending: boolean;
}

export function CreateReportModal({
  card,
  open,
  onClose,
  onCreate,
  isPending,
}: CreateReportModalProps) {
  const [narrative, setNarrative] = useState("");
  const [error, setError] = useState<string | null>(null);
  const narrativeField = NARRATIVE_FIELD[card.code];

  const create = async () => {
    setError(null);
    try {
      await onCreate(narrative.trim() === "" ? null : narrative.trim());
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Create ${card.code} report`}
      description={card.title}
      footer={
        <>
          {error && (
            <p
              className="text-caption text-status-error m-0 mr-auto"
              role="alert"
              data-testid="create-report-error"
            >
              {error}
            </p>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void create()}
            loading={isPending}
            data-testid="create-report-confirm"
          >
            Create draft report
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <div>
          <div className="text-caption uppercase tracking-wide text-gray-mid">
            Reporting period
          </div>
          <div className="text-small font-medium" data-testid="create-report-period">
            {reportingPeriodLabel(card.cadence, card.nextPeriodEnd)} · due{" "}
            {formatCalendarDate(card.nextDueDate)}
          </div>
          <p className="text-caption text-gray-mid m-0 mt-space-1">
            All report sections are computed from platform data; the draft
            opens in a preview before it is submitted for review.
          </p>
        </div>
        {narrativeField && (
          <label className="flex flex-col gap-space-1">
            <span className="text-small font-medium">{narrativeField.label}</span>
            <Textarea
              value={narrative}
              onChange={(e) => setNarrative(e.target.value)}
              rows={4}
              data-testid="create-report-narrative"
            />
            <span className="text-caption text-gray-mid">{narrativeField.helper}</span>
          </label>
        )}
      </div>
    </Modal>
  );
}
